// 請求書リソースの宣言。既存の invoice.service をそのまま呼ぶ（ブラウザ側＝本人の権限で実行）。
//
// 【業務フロー】請求書は納品書からの継承作成のみ（ユーザー指示:「顧客の締め月に合わして請求継承」）。
// 発行日は直近の月末、支払期限は請求先の支払日マスタ計算式から自動算出する
// （InvoiceFormPage の自動入力と同一ロジック = billing-date ユーティリティを共用）。
//
// 【重要】AI は「下書き」を作るだけ。発行(issueInvoice)は絶対に呼ばない
// （設計書 §6: 請求はAIは下書きのみ。発行は経理権限の人の操作）。
import { z } from 'zod';
import {
  fetchInvoices,
  createInvoiceFromDelivery,
  updateInvoice,
  type InvoiceUpdateInput,
} from '@modules/invoice/services/invoice.service';
import { fetchDeliveries } from '@modules/delivery/services/delivery.service';
import { computeInvoiceIssueDate, computeInvoiceDueDate } from '@modules/invoice/utils/billing-date';
import { fetchCustomer } from '@modules/customer/services/customer.service';
import { fetchPaymentDates } from '@modules/master/services/paymentDate.service';
import type { ID, Invoice } from '@shared/types';
import type { ListFilters, ResourceDescriptor, WriteResult } from './types';
import {
  MAX_ROWS,
  aiDocItemSchema,
  calcDocTotals,
  dateString,
  normalizeCode,
  resolveEmployee,
  toDateString,
  toDocLineItems,
  toTimestamp,
} from './shared';

/** 請求書1件を、LLM が読む最小限の形へ射影する。 */
function toCompactInvoice(inv: Invoice, customerName: string | null) {
  return {
    code: inv.code,
    customer: customerName, // Invoice は customerId しか持たないため名前解決して渡す
    status: inv.status,
    total: inv.grandtotal, // 税込合計
    issueDate: toDateString(inv.issueDate),
  };
}

/** 請求書の customerId → 会社名を解決する（失敗しても一覧は返す）。 */
async function resolveCustomerName(customerId: ID): Promise<string | null> {
  const customer = await fetchCustomer(customerId).catch(() => null);
  return customer?.name ?? null;
}

async function list(filters: ListFilters): Promise<unknown> {
  const invoices = await fetchInvoices();

  // Invoice に title は無いため、keyword は請求書番号で絞る
  const keyword = filters.keyword;
  const filtered = keyword ? invoices.filter((inv) => inv.code.includes(keyword)) : invoices;

  const rows = await Promise.all(
    filtered.slice(0, MAX_ROWS).map(async (inv) => toCompactInvoice(inv, await resolveCustomerName(inv.customerId)))
  );
  return {
    count: filtered.length,
    truncated: filtered.length > MAX_ROWS,
    invoices: rows,
  };
}

async function get(rawCode: string): Promise<unknown> {
  const code = normalizeCode(rawCode);
  const invoices = await fetchInvoices();
  const found = invoices.find((inv) => normalizeCode(inv.code) === code);
  if (!found) {
    return { found: false, note: `請求書番号「${rawCode}」は見つかりませんでした` };
  }
  return { found: true, invoice: toCompactInvoice(found, await resolveCustomerName(found.customerId)) };
}

// ── 書込（C-2）─────────────────────────────────────────────────

const writeCreateSchema = z.object({
  deliveryCode: z.string().min(1, 'deliveryCode（納品書番号）は必須'),
});

const writeUpdateSchema = z
  .object({
    issueDate: dateString.optional(),
    dueDate: dateString.optional(),
    notes: z.string().optional(),
    assignee: z.string().min(1).optional(),
    /** 渡すと明細を「全て置き換える」（追記ではない）。金額は自動再計算される。 */
    items: z.array(aiDocItemSchema).min(1).max(30).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'changes に変更する項目を1つ以上入れる' });

/** code から更新対象の請求書を引き当てる（get と同じ正規化・完全一致のみ）。 */
async function findByCode(rawCode: string): Promise<Invoice | null> {
  const code = normalizeCode(rawCode);
  const invoices = await fetchInvoices();
  return invoices.find((inv) => normalizeCode(inv.code) === code) ?? null;
}

export const invoiceResource: ResourceDescriptor = {
  name: 'invoice',
  label: '請求書',
  list,
  get,
  write: {
    fields: [
      { name: 'deliveryCode', label: '納品書番号', type: 'ref', resource: 'delivery', required: true, note: '作成時のみ・継承元の納品書番号' },
      { name: 'issueDate', label: '発行日', type: 'date', note: '更新のみ。YYYY-MM-DD' },
      { name: 'dueDate', label: '支払期限', type: 'date', note: '更新のみ。YYYY-MM-DD' },
      { name: 'notes', label: '備考', type: 'string', note: '更新のみ' },
      { name: 'assignee', label: '担当者', type: 'string', note: '更新のみ。担当者名で指定（IDは不要）' },
      { name: 'items', label: '明細', type: 'items', note: '更新のみ。配列 [{name,quantity,unit?,unitPrice}]。全置き換え・金額は自動再計算' },
    ],
    createSchema: writeCreateSchema,
    // 発行(issueInvoice)・入金・締めは経理業務。AIには開放しない（締め済みは本体 updateInvoice が拒否する）
    updateSchema: writeUpdateSchema,
    async create(data, userId: ID): Promise<WriteResult> {
      const input = data as z.infer<typeof writeCreateSchema>;

      // ① 継承元の納品書を解決する（表記ゆれは normalizeCode で吸収）
      const code = normalizeCode(input.deliveryCode);
      const deliveries = await fetchDeliveries();
      const delivery = deliveries.find((d) => normalizeCode(d.code) === code);
      if (!delivery) {
        throw new Error(`納品書「${input.deliveryCode}」が見つかりません`);
      }

      // ② 発行日・支払期限を「顧客の締め設定」で先に計算する（InvoiceFormPage の自動入力と同一）。
      //   発行日 = 直近の月末、支払期限 = 支払日マスタの計算式（例「翌月末」）。
      //   計算式を解釈できない（「随時」等）場合は期限を触らない（継承時の既定値のまま）。
      const issueYmd = computeInvoiceIssueDate(new Date());
      const customer = await fetchCustomer(delivery.customerId).catch(() => null);
      const paymentDates = await fetchPaymentDates().catch(() => []);
      const formula = paymentDates.find((p) => p.id === customer?.paymentDateId)?.formula ?? '';
      const dueYmd = formula ? computeInvoiceDueDate(issueYmd, formula) : null;

      // ③ 継承作成（明細・金額・重複チェックは本体サービスの責務。status は 'draft'）。
      //   日付とレビュー待ちの印は overrides で「1回の作成」に原子的に含める
      //   （後追い更新だと、失敗時にタグ無しのAIレコードが残るため）
      const created = await createInvoiceFromDelivery(delivery.id, userId, {
        issueDate: toTimestamp(issueYmd),
        ...(dueYmd ? { dueDate: toTimestamp(dueYmd) } : {}),
        aiPendingReview: true,
      });
      // 発行(issueInvoice)はここでは絶対に呼ばない。AIが作るのは下書きまで（人がレビューして発行する）
      return { id: created.id, code: created.code };
    },
    async update(code, changes, userId: ID): Promise<WriteResult | null> {
      const target = await findByCode(code);
      if (!target) return null;
      const input = changes as z.infer<typeof writeUpdateSchema>;

      const patch: InvoiceUpdateInput = { aiPendingReview: true };
      if (input.issueDate !== undefined) patch.issueDate = toTimestamp(input.issueDate);
      if (input.dueDate !== undefined) patch.dueDate = toTimestamp(input.dueDate);
      if (input.notes !== undefined) patch.notes = input.notes;
      // 担当者は名前→ID解決（IDをLLMに組ませない）
      if (input.assignee !== undefined) patch.assignedUserId = (await resolveEmployee(input.assignee)).id;

      // 明細は「全置き換え」。金額は明細から再計算する（total/grandtotal は
      // createInvoiceFromDelivery と同じ対応: total = grandtotal = subtotal + advance + totalgst）
      if (input.items && input.items.length > 0) {
        const taxCalcMode = target.taxCalcMode ?? 'floor';
        const totals = calcDocTotals(input.items, taxCalcMode);
        const grandtotal = totals.subtotal + (target.advance ?? 0) + totals.taxAmount;
        patch.lineItems = toDocLineItems(input.items);
        patch.subtotal = totals.subtotal;
        patch.taxAmount = totals.taxAmount;
        patch.totalgst = totals.taxAmount;
        patch.total = grandtotal;
        patch.grandtotal = grandtotal;
      }

      // 発行(issueInvoice)はここでは絶対に呼ばない。締め済み（isLocked）の拒否は本体 updateInvoice の責務
      await updateInvoice(target.id, patch, userId);
      return { id: target.id, code: target.code };
    },
    link: (id) => `/invoices/${id}/edit`,
  },
};
